import { CartLine, CartProduct, CartState } from "../types/cart";

const KEY_PREFIX = "@ahcp/cart/v1";

export function cartStorageKey(userId?: string | number | null, email?: string | null): string {
  const id = String(userId ?? "").trim();
  if (id) return `${KEY_PREFIX}/user-${id}`;
  const mail = String(email ?? "").trim().toLowerCase();
  if (mail) return `${KEY_PREFIX}/email-${mail}`;
  return `${KEY_PREFIX}/guest`;
}

export function guestCartKey(): string {
  return `${KEY_PREFIX}/guest`;
}

export function emptyCart(): CartState {
  return { items: [], total: 0, itemCount: 0 };
}

export function calculateCartTotals(items: CartLine[]): CartState {
  const total = items.reduce(
    (sum, line) => sum + Number(line.product.price) * line.quantity,
    0
  );
  const itemCount = items.reduce((sum, line) => sum + line.quantity, 0);
  return { items, total, itemCount };
}

export function effectiveCap(product: CartProduct): number {
  const stock = Number(product.stock);
  const cap = Number(product.maxQtyPerCustomer);
  const stockOk = Number.isFinite(stock) ? stock : 0;
  if (Number.isFinite(cap) && cap > 0) {
    return Math.min(stockOk, cap);
  }
  return stockOk;
}

export function sanitiseCartLine(raw: unknown): CartLine | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const nested =
    row.product && typeof row.product === "object"
      ? (row.product as Record<string, unknown>)
      : row;

  const id = String(nested.id ?? nested.productId ?? "").trim();
  if (!id) return null;

  const price = Number(nested.price);
  if (!Number.isFinite(price) || price <= 0) return null;

  const quantity = Math.floor(Number(row.quantity ?? nested.quantity ?? 0));
  if (!Number.isFinite(quantity) || quantity < 1) return null;

  let stock = Number(nested.stock);
  if (!Number.isFinite(stock) || stock < 0) stock = 0;
  if (stock < quantity) stock = quantity;

  const name = String(nested.name ?? nested.title ?? nested.code ?? id).trim() || id;
  const title = String(nested.title ?? name).trim() || name;
  const code = String(nested.code ?? id).trim() || id;
  const category = String(nested.category ?? "General").trim() || "General";
  const maxQty = Number(nested.maxQtyPerCustomer);

  const product: CartProduct = {
    id,
    name,
    title,
    code,
    stock,
    batch: nested.batch ? String(nested.batch) : undefined,
    expiryDate: nested.expiryDate ? String(nested.expiryDate) : undefined,
    price,
    category,
    maxQtyPerCustomer:
      Number.isFinite(maxQty) && maxQty > 0 ? maxQty : undefined,
    description: nested.description ? String(nested.description) : undefined,
  };

  return { product, quantity };
}

export function loadCartFromStorage(key: string): CartState {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return emptyCart();
    const parsed = JSON.parse(raw) as { items?: unknown[] } | unknown[];
    const rows = Array.isArray(parsed)
      ? parsed
      : Array.isArray(parsed.items)
        ? parsed.items
        : [];
    const items = rows
      .map(sanitiseCartLine)
      .filter((line): line is CartLine => line != null);
    return calculateCartTotals(items);
  } catch {
    return emptyCart();
  }
}

export function saveCartToStorage(key: string, state: CartState): void {
  try {
    localStorage.setItem(key, JSON.stringify({ items: state.items }));
  } catch {
    /* ignore quota */
  }
}

export function mergeGuestCartIfNeeded(userKey: string): void {
  if (userKey === guestCartKey()) return;
  const existing = loadCartFromStorage(userKey);
  if (existing.items.length > 0) return;
  const guest = loadCartFromStorage(guestCartKey());
  if (guest.items.length === 0) return;
  saveCartToStorage(userKey, guest);
  localStorage.removeItem(guestCartKey());
}
